/**
 * OwnerPage — SaaS founder / Super-Admin dashboard.
 *
 * ONLY renders content to users whose email matches the backend's
 * OWNER_EMAIL env var. Any other visitor sees a friendly "access denied"
 * screen (server also enforces via 403, so this is defense-in-depth).
 *
 * Sections:
 *   • Overview KPIs — tenants, billing distribution, MRR/ARR, activity.
 *   • 30-day sparkline — daily bills + signups (compact SVG chart).
 *   • Tenants table — searchable, filterable by billing state, drill-down.
 *   • Tenant drill-down drawer — per-tenant billing + usage details.
 */
import { useEffect, useMemo, useState } from 'react';
import { Users, Receipt, HardDrive, TrendingUp, ShieldCheck, Search, X, RefreshCw, ExternalLink, Calendar, Wallet, Eye, EyeOff, Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';
import { OwnerManageAccessCard } from '../components/OwnerManageAccessCard';
import { api, fmtDate, fmtMoney, fmtDateTime } from '../lib/api';

const STATE_COLORS = {
    trial:     'bg-[hsl(var(--info-bg))] text-[hsl(var(--info-fg))] border-[hsl(var(--info-border))]',
    grace:     'bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border-[hsl(var(--warning-border))]',
    active:    'bg-[hsl(var(--success-bg))] text-[hsl(var(--success-fg))] border-[hsl(var(--success-border))]',
    past_due:  'bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border-[hsl(var(--warning-border))]',
    locked:    'bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger-fg))] border-[hsl(var(--danger-border))]',
    unknown:   'bg-muted text-muted-foreground border-border',
};

function formatBytes(n) {
    if (!n || n <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0; let v = Number(n);
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function KpiCard({ icon: Icon, label, value, hint, testid, tone = 'default' }) {
    const toneCls = tone === 'accent' ? 'bg-primary/10 text-primary' : 'bg-accent text-accent-foreground';
    return (
        <Card className="p-4 md:p-5 border-border/80" data-testid={testid}>
            <div className="flex items-center gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${toneCls}`}>
                    <Icon size={18} strokeWidth={1.75} />
                </div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            </div>
            <div className="mt-3 font-heading text-2xl md:text-3xl tabular-nums" data-testid={`${testid}-value`}>
                {value}
            </div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </Card>
    );
}

function StatePill({ state }) {
    const cls = STATE_COLORS[state] || STATE_COLORS.unknown;
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${cls}`}>
            {state || 'unknown'}
        </span>
    );
}

/** Compact 30-day SVG sparkline for bills (bars) + signups (line). */
function Sparkline({ series }) {
    if (!series || series.length === 0) return null;
    const w = 720, h = 120, pad = 24;
    const maxBills = Math.max(1, ...series.map((d) => d.bills));
    const maxSignups = Math.max(1, ...series.map((d) => d.signups));
    const bw = (w - pad * 2) / series.length;
    // Bars (bills)
    const bars = series.map((d, i) => {
        const bh = (d.bills / maxBills) * (h - pad * 2);
        const x = pad + i * bw + bw * 0.15;
        const y = h - pad - bh;
        return <rect key={`b${i}`} x={x} y={y} width={bw * 0.7} height={bh} rx={2} className="fill-primary/60" />;
    });
    // Line (signups)
    const points = series.map((d, i) => {
        const x = pad + i * bw + bw / 2;
        const y = h - pad - (d.signups / maxSignups) * (h - pad * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');

    return (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32">
            {bars}
            <polyline points={points} fill="none" strokeWidth="2" className="stroke-foreground/80" />
            {series.map((d, i) => {
                const x = pad + i * bw + bw / 2;
                const y = h - pad - (d.signups / maxSignups) * (h - pad * 2);
                return <circle key={`p${i}`} cx={x} cy={y} r={2} className="fill-foreground/80" />;
            })}
        </svg>
    );
}

export default function OwnerPage() {
    const [access, setAccess] = useState(null); // null=loading, true=owner, false=denied
    const [overview, setOverview] = useState(null);
    const [tenants, setTenants] = useState(null);
    const [q, setQ] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [openTenant, setOpenTenant] = useState(null);
    const [tenantDetail, setTenantDetail] = useState(null);
    const [tenantLoading, setTenantLoading] = useState(false);
    // Trust Sprint: every tenant drill-down is audited. We prompt for a
    // reason before opening, and default to a masked view.
    const [reasonPromptEmail, setReasonPromptEmail] = useState(null);
    const [reasonInput, setReasonInput] = useState('');
    const [currentReason, setCurrentReason] = useState('');

    async function loadAll() {
        setRefreshing(true);
        try {
            const [ov, tn] = await Promise.all([
                api.ownerOverview(),
                api.ownerTenants({ q: q || undefined, state: stateFilter || undefined }),
            ]);
            setOverview(ov);
            setTenants(tn.tenants || []);
        } catch (e) {
            const s = e?.response?.status;
            if (s === 403) {
                setAccess(false);
                return;
            }
            toast.error(e?.response?.data?.detail || 'Failed to load owner dashboard');
        } finally {
            setRefreshing(false);
        }
    }

    // On mount: probe /whoami first, then load if owner.
    useEffect(() => {
        (async () => {
            try {
                await api.ownerWhoami();
                setAccess(true);
                await loadAll();
            } catch (e) {
                setAccess(false);
            }
        })();
        // eslint-disable-next-line
    }, []);

    // Reload tenants when filters change (debounced by typing pause)
    useEffect(() => {
        if (access !== true) return;
        const t = setTimeout(loadAll, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q, stateFilter]);

    async function openTenantDetail(email, { reason, unmask } = {}) {
        setOpenTenant(email);
        setTenantDetail(null);
        setTenantLoading(true);
        setCurrentReason(reason || '');
        try {
            const d = await api.ownerTenantDetail(email, { reason, unmask });
            setTenantDetail(d);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load tenant');
        } finally {
            setTenantLoading(false);
        }
    }

    // Trust Sprint: intercept the row click to demand a reason first.
    function requestTenantDrilldown(email) {
        setReasonPromptEmail(email);
        setReasonInput('');
    }

    function confirmReasonAndOpen() {
        const reason = reasonInput.trim();
        if (!reason) {
            toast.error('Please enter a reason — this will be recorded.');
            return;
        }
        const email = reasonPromptEmail;
        setReasonPromptEmail(null);
        // Always masked by default; user can click "Unmask" inside the drawer.
        openTenantDetail(email, { reason, unmask: false });
    }

    async function unmaskCurrentTenant() {
        if (!openTenant || !currentReason) {
            toast.error('Missing reason — reopen the tenant with a reason.');
            return;
        }
        setTenantLoading(true);
        try {
            const d = await api.ownerTenantDetail(openTenant, { reason: currentReason, unmask: true });
            setTenantDetail(d);
            toast.success('Full view unmasked — this action was logged.');
        } catch (e) {
            toast.error('Failed to unmask');
        } finally {
            setTenantLoading(false);
        }
    }

    if (access === false) {
        return (
            <div className="max-w-xl mx-auto py-16 px-4" data-testid="owner-access-denied">
                <Card className="p-8 border-border/80 text-center space-y-3">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger-fg))] mx-auto">
                        <ShieldCheck size={22} />
                    </div>
                    <div className="font-heading text-xl">Owner access only</div>
                    <div className="text-sm text-muted-foreground">
                        This dashboard is reserved for the platform owner. Your account does not have permission to view it.
                    </div>
                </Card>
            </div>
        );
    }

    if (access === null || !overview) {
        return (
            <div className="space-y-4 animate-fade-in" data-testid="owner-loading">
                <Skeleton className="h-14" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-64" />
            </div>
        );
    }

    const { tenants: t, billing: bl, activity: ac, series_30d: series } = overview;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="owner-page">
            <PageHeader
                title="Owner dashboard"
                subtitle="Cross-tenant KPIs for the SaaS platform. Read-only. All figures include every workspace on this deployment."
                actions={
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={loadAll}
                        disabled={refreshing}
                        className="gap-2"
                        data-testid="owner-refresh"
                    >
                        <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
                    </Button>
                }
            />

            {/* Row 1 — tenant KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={Users} label="Tenants" value={t.total} hint={`${t.verified} verified`} testid="owner-kpi-tenants" tone="accent" />
                <KpiCard icon={TrendingUp} label="New (7d)" value={t.new_last_7d} hint={`${t.new_last_30d} in 30d`} testid="owner-kpi-new" />
                <KpiCard icon={Wallet} label="MRR" value={fmtMoney(bl.mrr_inr, 'INR')} hint={`${bl.subscriptions} active subs`} testid="owner-kpi-mrr" tone="accent" />
                <KpiCard icon={Calendar} label="ARR" value={fmtMoney(bl.arr_inr, 'INR')} hint={`Plan ${bl.plan_name} · ${fmtMoney(bl.plan_amount_inr, 'INR')}/mo`} testid="owner-kpi-arr" />
            </div>

            {/* Row 2 — activity KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={Receipt} label="Total bills" value={ac.total_bills.toLocaleString()} testid="owner-kpi-bills" />
                <KpiCard icon={Wallet} label="Total spend processed" value={fmtMoney(ac.total_spend_inr, 'INR')} testid="owner-kpi-spend" />
                <KpiCard icon={HardDrive} label="Storage used" value={formatBytes(ac.storage_bytes)} hint="GridFS (bills + PDFs)" testid="owner-kpi-storage" />
                <Card className="p-4 md:p-5 border-border/80" data-testid="owner-billing-breakdown">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Billing states</div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                        {Object.entries(bl.state_counts).map(([k, v]) => (
                            v > 0 ? (
                                <div key={k} className={`inline-flex items-center gap-1.5 border rounded-full px-2 py-1 text-xs ${STATE_COLORS[k] || STATE_COLORS.unknown}`}>
                                    <span className="uppercase text-[9px] tracking-wide">{k}</span>
                                    <span className="font-mono tabular-nums">{v}</span>
                                </div>
                            ) : null
                        ))}
                    </div>
                </Card>
            </div>

            {/* Row 2b — business activity KPIs (Sprint 2026-07) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard
                    icon={Receipt}
                    label="Invoices generated"
                    value={(ac.invoices_count ?? 0).toLocaleString()}
                    hint={fmtMoney(ac.invoices_value_inr ?? 0, 'INR') + ' invoiced'}
                    testid="owner-kpi-invoices"
                    tone="accent"
                />
                <KpiCard
                    icon={Wallet}
                    label="Expenses recorded"
                    value={(ac.expenses_count ?? 0).toLocaleString()}
                    hint={fmtMoney(ac.expenses_value_inr ?? 0, 'INR') + ' recorded'}
                    testid="owner-kpi-expenses"
                />
                <KpiCard
                    icon={Users}
                    label="Salaries paid"
                    value={fmtMoney(ac.salaries_paid_inr ?? 0, 'INR')}
                    hint="Payroll + advances tracked"
                    testid="owner-kpi-salaries"
                />
                <KpiCard
                    icon={Info}
                    label="Quotes & credit notes"
                    value={(ac.quotes_count ?? 0).toLocaleString()}
                    hint={`${fmtMoney(ac.credit_notes_value_inr ?? 0, 'INR')} in credit notes`}
                    testid="owner-kpi-quotes"
                />
            </div>

            {/* Row 3 — 30-day sparkline */}
            <Card className="p-5 border-border/80" data-testid="owner-sparkline">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="font-heading text-lg">Last 30 days</div>
                        <div className="text-xs text-muted-foreground">
                            <span className="inline-block h-2 w-3 rounded-sm bg-primary/60 align-middle mr-1" /> Bills uploaded
                            <span className="inline-block h-0.5 w-3 bg-foreground/80 align-middle ml-4 mr-1" /> Signups
                        </div>
                    </div>
                </div>
                <Sparkline series={series} />
            </Card>

            {/* Row 3b — Manage access (grant / revoke unlimited scans, provision test accounts) */}
            <OwnerManageAccessCard />

            {/* Row 4 — Tenants table */}
            <Card className="border-border/80 overflow-hidden">
                <div className="p-4 border-b border-border flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                    <div>
                        <div className="font-heading text-lg">All tenants</div>
                        <div className="text-xs text-muted-foreground">Click a row to open drill-down</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                data-testid="owner-search"
                                placeholder="Search email…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                className="pl-8 h-9 w-56"
                            />
                        </div>
                        <select
                            data-testid="owner-state-filter"
                            value={stateFilter}
                            onChange={(e) => setStateFilter(e.target.value)}
                            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                        >
                            <option value="">All states</option>
                            <option value="trial">Trial</option>
                            <option value="grace">Grace</option>
                            <option value="active">Active</option>
                            <option value="past_due">Past due</option>
                            <option value="locked">Locked</option>
                        </select>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="owner-tenants-table">
                        <thead className="bg-muted/40 text-muted-foreground">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Email</th>
                                <th className="text-left px-3 py-2 font-medium">Business</th>
                                <th className="text-left px-3 py-2 font-medium">State</th>
                                <th className="text-right px-3 py-2 font-medium" title="Bills scanned (lifetime · this month)">Bills</th>
                                <th className="text-right px-3 py-2 font-medium" title="Invoices generated by this tenant to their customers">Invoices</th>
                                <th className="text-right px-3 py-2 font-medium" title="Expenses recorded by this tenant (count · total)">Expenses</th>
                                <th className="text-right px-3 py-2 font-medium" title="Total salaries + advances paid out by this tenant">Salaries</th>
                                <th className="text-right px-3 py-2 font-medium" title="Estimated infrastructure cost we incurred to serve this tenant · this month">Cost/mo</th>
                                <th className="text-right px-3 py-2 font-medium" title="Estimated lifetime infrastructure cost">Lifetime cost</th>
                                <th className="text-left px-3 py-2 font-medium">Signed up</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {(tenants || []).map((row) => (
                                <tr
                                    key={row.email}
                                    className="border-t border-border hover:bg-muted/40 cursor-pointer"
                                    onClick={() => requestTenantDrilldown(row.email)}
                                    data-testid={`owner-row-${row.email}`}
                                >
                                    <td className="px-3 py-2">
                                        <div className="font-mono text-xs">{row.email}</div>
                                        {row.email_verified && <div className="text-[10px] text-muted-foreground">verified</div>}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="text-xs">{row.business_name || <span className="text-muted-foreground">—</span>}</div>
                                        {row.pan && <div className="text-[10px] text-muted-foreground font-mono">{row.pan}</div>}
                                    </td>
                                    <td className="px-3 py-2"><StatePill state={row.state} /></td>
                                    <td className="px-3 py-2 text-right tabular-nums font-mono">
                                        <div>{row.bills_count.toLocaleString()}</div>
                                        {row.bills_this_month > 0 && (
                                            <div className="text-[10px] text-muted-foreground">+{row.bills_this_month} this mo</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-mono">
                                        <div>{(row.invoices_count || 0).toLocaleString()}</div>
                                        {row.invoices_value_inr > 0 && (
                                            <div className="text-[10px] text-muted-foreground">{fmtMoney(row.invoices_value_inr, 'INR')}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-mono">
                                        <div>{(row.expenses_count || 0).toLocaleString()}</div>
                                        {row.expenses_value_inr > 0 && (
                                            <div className="text-[10px] text-muted-foreground">{fmtMoney(row.expenses_value_inr, 'INR')}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-mono text-xs">
                                        {row.salaries_paid_inr > 0 ? fmtMoney(row.salaries_paid_inr, 'INR') : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-mono text-xs text-amber-700 dark:text-amber-500">
                                        {fmtMoney(row.cost_this_month_inr || 0, 'INR')}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-mono text-xs">
                                        <div className="text-amber-700 dark:text-amber-500">{fmtMoney(row.cost_lifetime_inr || 0, 'INR')}</div>
                                        {row.months_active > 1 && (
                                            <div className="text-[10px] text-muted-foreground">{row.months_active} mo active</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs">{fmtDate(row.created_at)}</td>
                                    <td className="px-2 py-2 text-right">
                                        <ExternalLink size={14} className="text-muted-foreground inline" />
                                    </td>
                                </tr>
                            ))}
                            {(!tenants || tenants.length === 0) && (
                                <tr>
                                    <td colSpan={11} className="px-3 py-8 text-center text-muted-foreground text-sm" data-testid="owner-tenants-empty">
                                        No tenants match your filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Tenant drill-down drawer */}
            <Sheet open={!!openTenant} onOpenChange={(v) => { if (!v) { setOpenTenant(null); setTenantDetail(null); setCurrentReason(''); } }}>
                <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="owner-tenant-drawer">
                    <SheetHeader>
                        <SheetTitle className="font-heading">{openTenant}</SheetTitle>
                    </SheetHeader>
                    {/* Trust Sprint: audit banner */}
                    <div
                        className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3 flex items-start gap-3 text-xs"
                        data-testid="owner-audit-banner"
                    >
                        <Info size={14} className="shrink-0 text-primary mt-0.5" />
                        <div className="min-w-0 flex-1">
                            <div className="font-medium text-foreground mb-0.5">This access is being logged and shown to the tenant.</div>
                            <div className="text-muted-foreground">
                                Reason recorded: <span className="italic">&ldquo;{currentReason || '—'}&rdquo;</span>
                                {tenantDetail?.masked === false && <> · <span className="text-destructive font-medium">Full view (unmasked)</span></>}
                                {tenantDetail?.masked === true && <> · <span>Masked view</span></>}
                            </div>
                        </div>
                        {tenantDetail?.masked && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={unmaskCurrentTenant}
                                data-testid="owner-unmask-btn"
                                className="shrink-0"
                            >
                                <Eye size={13} /> <span className="ml-1">Unmask</span>
                            </Button>
                        )}
                    </div>
                    {tenantLoading || !tenantDetail ? (
                        <div className="space-y-3 mt-4">
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                        </div>
                    ) : (
                        <div className="space-y-5 mt-4">
                            <section>
                                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Workspace</div>
                                <Card className="p-3 space-y-1 text-sm border-border/80">
                                    <div><span className="text-muted-foreground">Business:</span> {tenantDetail.workspace?.business_name || '—'}</div>
                                    <div><span className="text-muted-foreground">GSTIN:</span> <span className="font-mono">{tenantDetail.workspace?.gstin || '—'}</span></div>
                                    <div><span className="text-muted-foreground">PAN:</span> <span className="font-mono">{tenantDetail.workspace?.pan || '—'}</span></div>
                                    <div><span className="text-muted-foreground">Signed up:</span> {fmtDateTime(tenantDetail.created_at)}</div>
                                    <div><span className="text-muted-foreground">Email verified:</span> {tenantDetail.email_verified ? 'yes' : 'no'}</div>
                                </Card>
                            </section>
                            <Separator />
                            <section>
                                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Billing</div>
                                <Card className="p-3 space-y-1 text-sm border-border/80">
                                    <div className="flex items-center gap-2"><span className="text-muted-foreground">State:</span> <StatePill state={tenantDetail.billing?.state} /></div>
                                    <div><span className="text-muted-foreground">Days left:</span> {tenantDetail.billing?.days_left ?? '—'}</div>
                                    <div><span className="text-muted-foreground">Razorpay status:</span> {tenantDetail.billing?.status || '—'}</div>
                                    <div><span className="text-muted-foreground">Subscription ID:</span> <span className="font-mono text-xs">{tenantDetail.billing?.subscription_id || '—'}</span></div>
                                    <div><span className="text-muted-foreground">Trial end:</span> {fmtDate(tenantDetail.billing?.trial_end)}</div>
                                    <div><span className="text-muted-foreground">Grace end:</span> {fmtDate(tenantDetail.billing?.grace_end)}</div>
                                    <div><span className="text-muted-foreground">Current period ends:</span> {fmtDate(tenantDetail.billing?.current_period_end)}</div>
                                </Card>
                            </section>
                            <Separator />
                            <section>
                                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Usage</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <Card className="p-3 border-border/80">
                                        <div className="text-[10px] uppercase text-muted-foreground">Bills</div>
                                        <div className="font-heading text-xl tabular-nums">{tenantDetail.usage?.bills?.toLocaleString() || 0}</div>
                                    </Card>
                                    <Card className="p-3 border-border/80">
                                        <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1">
                                            Spend
                                            {tenantDetail.masked && <EyeOff size={10} title="Masked" />}
                                        </div>
                                        <div className="font-heading text-xl tabular-nums">
                                            {tenantDetail.masked
                                                ? (tenantDetail.usage?.spend_bucket || '—')
                                                : fmtMoney(tenantDetail.usage?.spend, 'INR')}
                                        </div>
                                    </Card>
                                    <Card className="p-3 border-border/80">
                                        <div className="text-[10px] uppercase text-muted-foreground">Vendors</div>
                                        <div className="font-heading text-xl tabular-nums">{tenantDetail.usage?.vendor_count || 0}</div>
                                    </Card>
                                    <Card className="p-3 border-border/80">
                                        <div className="text-[10px] uppercase text-muted-foreground">Last upload</div>
                                        <div className="text-sm">{tenantDetail.usage?.last_upload ? fmtDateTime(tenantDetail.usage?.last_upload) : '—'}</div>
                                    </Card>
                                </div>
                            </section>
                            {tenantDetail.recent_invoices && tenantDetail.recent_invoices.length > 0 && (
                                <>
                                    <Separator />
                                    <section>
                                        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Recent invoices</div>
                                        <div className="space-y-1.5">
                                            {tenantDetail.recent_invoices.map((inv, i) => (
                                                <Card key={inv.id || i} className="p-2.5 border-border/80 text-xs flex items-center justify-between">
                                                    <div>
                                                        <div className="font-mono">{inv.id || inv.razorpay_invoice_id || '—'}</div>
                                                        <div className="text-muted-foreground">{fmtDate(inv.created_at)}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="font-mono tabular-nums">
                                                            {tenantDetail.masked
                                                                ? (inv.amount_bucket || '—')
                                                                : fmtMoney((inv.amount_paid || inv.amount || 0) / 100, 'INR')}
                                                        </div>
                                                        <div className="text-muted-foreground">{inv.status || '—'}</div>
                                                    </div>
                                                </Card>
                                            ))}
                                        </div>
                                    </section>
                                </>
                            )}
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            {/* Trust Sprint: reason-prompt dialog before drilling into a tenant. */}
            <AlertDialog
                open={!!reasonPromptEmail}
                onOpenChange={(v) => { if (!v) setReasonPromptEmail(null); }}
            >
                <AlertDialogContent data-testid="owner-reason-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Why are you accessing this workspace?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <div>
                                    You are about to view data for <span className="font-mono">{reasonPromptEmail}</span>.
                                    This access will be logged and shown to the tenant in their Settings → Access Log.
                                </div>
                                <div>
                                    <Label htmlFor="owner-reason-input" className="text-xs mb-1.5 block">
                                        Reason (required — will be visible to the tenant)
                                    </Label>
                                    <Input
                                        id="owner-reason-input"
                                        data-testid="owner-reason-input"
                                        placeholder='e.g. "Investigating support ticket #12345"'
                                        value={reasonInput}
                                        onChange={(e) => setReasonInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') confirmReasonAndOpen(); }}
                                        maxLength={280}
                                        autoFocus
                                    />
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Sensitive fields (invoice amounts, GSTIN) are masked by default. Click &ldquo;Unmask&rdquo; inside the drawer for a full view — that action is also logged.
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="owner-reason-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="owner-reason-confirm"
                            onClick={confirmReasonAndOpen}
                            disabled={!reasonInput.trim()}
                        >
                            Open workspace (masked)
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
